/* eslint-disable jsx-a11y/click-events-have-key-events */
import Form from 'antd/lib/form/Form';
import CircularProgress from 'components/CircularProgress';
import ButtonWithSpinner from 'components/common/ButtonWithSpinner';
import IconComponent from 'components/common/IconComponent';
import { InputComponent } from 'components/common/Input';
import { INPUT_TYPE } from 'components/common/Input/types';
import { newMessagePlaceholderId } from 'constants/messages';
import { Formik } from 'formik';
import useWindowSize from 'hooks/useWindowSize';
import { flatten } from 'lodash';
import React, { ReactElement, useMemo } from 'react';
import JoinAppointmentChat from 'routes/Transaction/components/TransactionMessageTab/AppointmentListInMessageTab/JoinAppointmentChat';
import { GeneralFunction } from 'types/common';
import { IChatRoom, IMessage } from 'types/messages';
import { mapRight } from 'utils';

import ChatCard from '../ChatBoxConversation/ChatCard';
import { useChatWindowProps } from './hooks';

const ChatWindow: React.FC<{
  messages?: IMessage[];
  oldMessages?: IMessage[];
  fetchNextPageMessage: GeneralFunction;
  loading?: boolean;
  onChatEnter?: GeneralFunction;
  chatAppointmentId?: string;
  onChatFocus?: GeneralFunction;
  refetchChatRoomData?: GeneralFunction;
  header?: JSX.Element | Element | ReactElement;
  mobileThreshold?: number;
  oldMessageLoading?: boolean;
  chatRoom?: IChatRoom;
  allowJoinChat?: boolean;
  isInChatRoom?: boolean;
  refetchMessageData?: GeneralFunction;
}> = ({
  messages,
  oldMessages,
  fetchNextPageMessage,
  loading,
  onChatEnter,
  chatAppointmentId,
  onChatFocus,
  refetchChatRoomData,
  header,
  mobileThreshold,
  chatRoom,
  oldMessageLoading,
  allowJoinChat,
  isInChatRoom,
  refetchMessageData,
}) => {
  const {
    onChatWindowScroll,
    chatWindowRef,
    formRef,
    onChatEnterDefault,
    onChatFocusDefault,
    chatboxMaxHeight,
    chatHeaderRef,
    chatInputHeight,
    chatImageHeight,
    setChatboxMaxHeight,
    chatContainerRef,
    chatboxImages,
    inputRef,
    onDeleteImageClick,
  } = useChatWindowProps({
    fetchNextPageMessage,
    loading,
    onChatEnter,
    chatAppointmentId,
    onChatFocus,
    messages,
    refetchChatRoomData,
    chatRoom,
    oldMessageLoading,
    oldMessages,
  });
  const { isMobile } = useWindowSize(mobileThreshold);

  const allMessages = useMemo(() => flatten([messages || [], oldMessages || []]), [messages, oldMessages]);

  return (
    <div
      className='w-full h-full flex flex-col'
      ref={(ref) => {
        if (!chatboxMaxHeight && chatHeaderRef.current && ref) {
          setChatboxMaxHeight(ref.clientHeight - chatInputHeight - chatHeaderRef.current.clientHeight);
        }
        chatContainerRef.current = ref;
      }}>
      {header && (
        <div
          className='border-b border-border_color'
          ref={(ref) => {
            chatHeaderRef.current = ref;
          }}>
          {header}
        </div>
      )}
      <div
        className='relative flex w-full flex-1 h-full overflow-hidden'
        style={{ paddingBottom: chatInputHeight + (chatboxImages.length > 0 ? chatImageHeight : 0) }}>
        <div
          style={{ maxHeight: mobileThreshold && isMobile ? chatboxMaxHeight || undefined : undefined }}
          className='chatboxes h-full w-full overflow-y-auto p-2'
          onScroll={onChatWindowScroll}
          ref={chatWindowRef}>
          {oldMessageLoading && (
            <div style={{ height: 50 }}>
              <CircularProgress />
            </div>
          )}
          {(!!chatboxMaxHeight || !isMobile) && messages && messages.length > 0 ? (
            <>
              {mapRight(allMessages, (message) => {
                const messageId = message.id || '';
                const isMessageSending = messageId?.includes(newMessagePlaceholderId);
                return <ChatCard isMessageSending={isMessageSending} key={message.id} message={message} />;
              })}
              {allowJoinChat && !isInChatRoom && (
                <div>
                  <JoinAppointmentChat
                    callback={() => {
                      refetchMessageData?.();
                      refetchChatRoomData?.();
                    }}
                    chatRoom={chatRoom}
                  />
                </div>
              )}
            </>
          ) : (
            !loading &&
            !!chatboxMaxHeight &&
            (allowJoinChat && !isInChatRoom ? (
              <div>
                <JoinAppointmentChat
                  callback={() => {
                    refetchMessageData?.();
                    refetchChatRoomData?.();
                  }}
                  chatRoom={chatRoom}
                />
              </div>
            ) : (
              <div className='w-full h-full p-4 text-xl'>No message</div>
            ))
          )}
          {/* loading for first fetch of messages */}
          {!messages && loading && <CircularProgress />}
        </div>
        <div className='chat-input'>
          <Formik innerRef={formRef} onSubmit={onChatEnterDefault} initialValues={{ chat: '' }}>
            <Form autoComplete='off'>
              {chatboxImages.length > 0 && (
                <div className='chat-image-container'>
                  {chatboxImages.map(([imgId, img]) => {
                    const imgSrc = URL.createObjectURL(img);
                    return (
                      <div className='chat-image last:mr-0 mr-4' key={`${imgId}`}>
                        <div className='close-icon' onClick={() => onDeleteImageClick(imgId)}>
                          x
                        </div>
                        <img src={imgSrc} alt='pasted img' />
                      </div>
                    );
                  })}
                </div>
              )}
              <div className='chat-input'>
                <div className='gx-flex-1' ref={inputRef} onFocus={onChatFocusDefault}>
                  <InputComponent disabled={allowJoinChat && !isInChatRoom} type={INPUT_TYPE.TEXT} name='chat' />
                </div>
                <ButtonWithSpinner
                  onClick={() => {
                    formRef.current?.handleSubmit?.();
                  }}
                  htmlType='submit'
                  width='auto'
                  border='none'
                  buttonType='text'>
                  <div className='gx-d-flex gx-align-items-center'>
                    <IconComponent name='chat-bubble' />
                  </div>
                </ButtonWithSpinner>
              </div>
            </Form>
          </Formik>
        </div>
      </div>
    </div>
  );
};
export default React.memo(ChatWindow);
